import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import type {
  ApiClient, HeizungNetzbrueckenende, HeizungNetzbrueckenliste,
  HeizungNetzbrueckenzeile,
} from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, StatusBadge, formatiereZahl, type Spalte,
} from '../muster';

/**
 * Die Netzbrücken — was das Netz zusammenhält.
 *
 * ═══ Warum es diese Seite gibt ════════════════════════════════════════════
 *
 * `heizung_netzbruecken` trägt Verbindungen, die im MODELL fehlen und
 * trotzdem gerechnet werden. Der Rohrnetzgraph liest sie und hängt die
 * aktiven als Kanten ein; ohne sie zerfällt der Bauteilgraph in Inseln, und
 * mit ihnen entstehen Wege, die im Modell keine Leitung haben.
 *
 * Gelesen wurde die Tabelle von der Rechnung und von der Mängelliste. In der
 * Oberfläche gab es dazu keinen einzigen Treffer: Niemand konnte sehen, was
 * das Netz zusammenhält — und damit auch nicht, welche Förderhöhe auf einer
 * angenommenen Verbindung beruht.
 *
 * ═══ Warum hier nichts bestätigt wird ═════════════════════════════════════
 *
 * Eine Brücke zu aktivieren heißt zu behaupten, dass zwei Rohre in
 * Wirklichkeit verbunden sind. Das ist eine PLANUNGSENTSCHEIDUNG und keine
 * Bildschirmhandlung. Diese Seite zeigt und zählt; sie hat bewusst keinen
 * Knopf dafür.
 *
 * ═══ Was die Zahlen sagen und was nicht ═══════════════════════════════════
 *
 * Der Abstand ist der geometrische Abstand der beiden Enden. Er ist der
 * BELEG dafür, dass Leitung fehlt und nicht bloß ein Anschluss nicht gelesen
 * wurde: Zehn Meter sind keine Anschlusstoleranz. Der Druckverlust dieser
 * fehlenden Leitung ist in KEINER Rechnung enthalten — Länge, Weite und
 * Formstücke sind unbekannt. Jeder Weg über eine Brücke ist deshalb eine
 * untere Schranke.
 */

interface Rahmen { api: ApiClient; projekt: { id: string } }

// Der Abstand kommt in mm und steht in m (TH-2b: formatiereZahl statt toLocaleString, TX-STANDARD S1.7).
const mm = (wert: number | null | undefined) =>
  wert === null || wert === undefined ? '—' : formatiereZahl(wert / 1000, 2);

/** Eine Zeile der Übersicht je Systemgruppe. */
type Gruppenzeile = HeizungNetzbrueckenliste['summe']['jeGruppe'][number];

const GRUPPENSPALTEN: Spalte<Gruppenzeile>[] = [
  { schluessel: 'gruppe', titel: 'Systemgruppe', immerSichtbar: true, wert: (g) => g.systemgruppe },
  { schluessel: 'bruecken', titel: 'Brücken', art: 'zahl', nachkomma: 0, wert: (g) => g.bruecken },
  { schluessel: 'aktiv', titel: 'aktiv', art: 'zahl', nachkomma: 0, wert: (g) => g.aktiv },
  { schluessel: 'unbestaetigt', titel: 'unbestätigt', art: 'zahl', nachkomma: 0, wert: (g) => g.unbestaetigt },
];

/** Ein Ende in einer Zelle: was es ist, wo es liegt, welche Seite es führt. */
function Ende({ ende }: { ende: HeizungNetzbrueckenende }) {
  if (ende.herkunft === 'unbekannt') {
    return (
      <span title={'Weder Rohr noch Bauteil: Die Brücke hängt an einem '
        + 'Element, das die Aufnahme nicht (mehr) kennt.'}>
        <StatusBadge zustand="fehler" klein text="unbekannt" />
        {' '}
        <span className="sehr-schwach">{ende.elementId}</span>
      </span>
    );
  }

  return (
    <span>
      <StatusBadge klein zustand="neutral" text={ende.art ?? ende.herkunft} />
      {' '}
      {ende.bezeichnung ?? <span className="sehr-schwach">ohne Bezeichnung</span>}
      {ende.systemabkuerzung && (
        <span className="sehr-schwach"> · {ende.systemabkuerzung}</span>
      )}
      {ende.geschoss && <span className="sehr-schwach"> · {ende.geschoss}</span>}
      {ende.netzrolle === 'quelle' && (
        <> <StatusBadge zustand="gut" klein text="Quelle" /></>
      )}
    </span>
  );
}

export function HeizungNetzbruecken() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { id } = useParams<{ id: string }>();

  const [daten, setDaten] = useState<HeizungNetzbrueckenliste | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [nurOffen, setNurOffen] = useState(false);

  const laden = async () => {
    try {
      setDaten(await api.heizungNetzbruecken(projekt.id));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { void laden(); }, [api, projekt.id]);

  const zeilen = useMemo(
    () => (daten?.zeilen ?? []).filter((z) => !nurOffen || !z.aktiv),
    [daten, nurOffen]);

  const spalten: Spalte<HeizungNetzbrueckenzeile>[] = [
    {
      schluessel: 'zustand', titel: 'Zustand', art: 'status', breite: '150px',
      immerSichtbar: true,
      wert: (z) => (z.aktiv ? 'aktiv (bestätigt)' : 'Vorschlag'),
      inhalt: (z) => (
        <StatusBadge
          zustand={z.aktiv ? 'warnung' : 'neutral'}
          klein
          text={z.aktiv ? 'aktiv · bestätigt' : 'Vorschlag'}
          titel={z.aktiv
            ? 'Wirkt im Graphen: Die Rechnung führt Wege über diese '
              + 'Verbindung. Ihr Druckverlust ist NICHT enthalten.'
            : 'Wirkt nicht. Ein Vorschlag ist keine Verbindung — ihn zu '
              + 'bestätigen ist eine Planungsentscheidung.'}
        />
      ),
    },
    {
      schluessel: 'systemgruppe', titel: 'Systemgruppe',
      wert: (z) => z.systemgruppe ?? '(ohne)',
      inhalt: (z) => z.systemgruppe
        ?? <span className="sehr-schwach">(ohne)</span>,
    },
    {
      schluessel: 'von', titel: 'Bauteil A', breite: '28%',
      wert: (z) => z.von.bezeichnung ?? String(z.von.elementId),
      inhalt: (z) => <Ende ende={z.von} />,
    },
    {
      schluessel: 'nach', titel: 'Bauteil B', breite: '28%',
      wert: (z) => z.nach.bezeichnung ?? String(z.nach.elementId),
      inhalt: (z) => <Ende ende={z.nach} />,
    },
    {
      schluessel: 'abstand', titel: 'Abstand', art: 'zahl', einheit: 'm',
      nachkomma: 2,
      wert: (z) => (z.abstandMm == null ? null : z.abstandMm / 1000),
      inhalt: (z) => (
        <span title={'Der geometrische Abstand der beiden Enden. Er ist der '
          + 'Beleg dafür, dass Leitung fehlt — und genau diese Leitung fehlt '
          + 'auch im Druckverlust.'}>
          {mm(z.abstandMm)}
        </span>
      ),
    },
    {
      schluessel: 'quelle', titel: 'Quelle', versteckt: true,
      wert: (z) => z.quelle,
    },
    {
      schluessel: 'begruendung', titel: 'Begründung', versteckt: true,
      wert: (z) => z.begruendung,
    },
  ];

  const kopf = (
    <PageHeader
      titel="Netzbrücken"
      beschreibung={'Verbindungen, die im Modell fehlen und trotzdem '
        + 'gerechnet werden. Nur die aktiven wirken; ihr Druckverlust ist in '
        + 'keiner Summe enthalten — jeder Weg darüber ist eine untere '
        + 'Schranke.'}
      meta={(
        <Link to={`/projekte/${id}/heizung-berechnung`}>← Rohrnetz</Link>
      )}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState titel="Netzbrücken nicht ladbar" meldung={fehler}
                    beiErneutVersuchen={() => void laden()} />
      </>
    );
  }

  if (!daten) return <>{kopf}<LoadingState text="Netzbrücken werden geladen …" /></>;

  const s = daten.summe;

  return (
    <>
      {kopf}

      <KpiReihe>
        <KpiCard titel="Netzbrücken" wert={s.bruecken}
                 vergleich="in heizung_netzbruecken" />
        <KpiCard titel="aktiv" wert={s.aktiv}
                 zustand={s.aktiv > 0 ? 'warnung' : 'neutral'}
                 zustandText={s.aktiv > 0 ? 'wirkt in der Rechnung' : 'keine'}
                 vergleich={`${s.aktiv} von ${s.bruecken} — so viele `
                   + 'Verbindungen rechnet das Netz, die das Modell nicht hat'} />
        <KpiCard titel="unbestätigte Vorschläge" wert={s.unbestaetigt}
                 vergleich="wirken nicht — Bestätigen ist eine Planungsentscheidung" />
        <KpiCard titel="größter Abstand" wert={mm(s.abstandMaxMm)} einheit="m"
                 vergleich={`Vorschlagsgrenze ${mm(s.grenzeAbstandMm)} m — `
                   + 'darüber ist es kein vergessener Anschluss, sondern ein '
                   + 'anderes System'} />
      </KpiReihe>

      <ContentSection
        titel="Je Systemgruppe"
        beschreibung={'Wo das Netz zusammengehalten wird. Eine Gruppe mit '
          + 'vielen aktiven Brücken hat ihre Wege nicht aus dem Modell, '
          + 'sondern aus dieser Tabelle.'}
      >
        <DataTable
          name="Netzbrücken je Systemgruppe"
          spalten={GRUPPENSPALTEN}
          zeilen={s.jeGruppe}
          schluessel={(g) => g.systemgruppe}
          suche={false}
          dicht
          leer={<EmptyState titel="Keine Netzbrücke vorhanden" />}
        />
      </ContentSection>

      <ContentSection
        titel="Die Brücken"
        beschreibung={'Je Brücke die beiden Bauteile, ihr Abstand, ihre '
          + 'Systemgruppe und ob sie wirkt. Bestätigt wird hier nichts.'}
        aktionen={(
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={nurOffen}
                   onChange={(e) => setNurOffen(e.target.checked)} />
            nur unbestätigte
          </label>
        )}
      >
        <DataTable
          name="heizung-netzbruecken"
          spalten={spalten}
          zeilen={zeilen}
          schluessel={(z) => z.id}
          trefferText="Brücken"
          leer={<EmptyState titel={nurOffen
            ? 'Keine unbestätigte Netzbrücke'
            : 'Keine Netzbrücke vorhanden'} />}
        />
      </ContentSection>
    </>
  );
}
